/**
 * HomePage.jsx — arhat.info
 * Focus: Job Search + Freelancer AI portals
 * Secondary: All free tools in one grid section
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';

const T = {
  bg:     '#07080c',
  bg2:    '#0c0f18',
  surf:   '#161c2a',
  border: 'rgba(255,255,255,.07)',
  white:  '#dce6fa',
  muted:  'rgba(220,230,250,.55)',
  dim:    'rgba(220,230,250,.28)',
  cyan:   '#00c8f0',
  blue:   '#4d8fff',
  green:  '#2ecc71',
  yellow: '#f1c40f',
  purple: '#b78cff',
  orange: '#e67e22',
  teal:   '#1abc9c',
  sans:   '"Syne","DM Sans",system-ui,sans-serif',
  mono:   '"JetBrains Mono",monospace',
};

// ── Two main AI portals ────────────────────────────────────────────
const PORTALS = [
  {
    id:      'job-search',
    path:    '/job-search',
    label:   'AI Job Search Copilot',
    tagline: 'Land more interviews',
    icon:    '🎯',
    color:   T.cyan,
    glow:    'rgba(0,200,240,.18)',
    badge:   'Most Popular',
    desc:    'Match your resume to any job, generate tailored cover letters, reply to recruiters, and optimise ATS keywords — all in seconds.',
    tools:   [
      { icon:'🎯', label:'Job Matcher',     desc:'Score your resume against any JD'    },
      { icon:'📄', label:'Cover Letter',    desc:'Tailored letters in 10 seconds'       },
      { icon:'📋', label:'Resume Summary',  desc:'ATS-optimised professional summary'   },
      { icon:'🤝', label:'Recruiter Reply', desc:'Professional responses instantly'     },
      { icon:'💼', label:'LinkedIn Post',   desc:'Posts that get noticed'               },
      { icon:'📧', label:'Email Generator', desc:'Cold outreach and follow-ups'         },
    ],
    cta: 'Start job search →',
  },
  {
    id:      'freelancer',
    path:    '/freelancer',
    label:   'Freelancer AI Toolkit',
    tagline: 'Win more contracts',
    icon:    '💼',
    color:   T.purple,
    glow:    'rgba(183,140,255,.18)',
    badge:   null,
    desc:    'Write winning Upwork proposals, reach out to clients on LinkedIn, send follow-up emails, and calculate your ideal freelance rate.',
    tools:   [
      { icon:'🏆', label:'Upwork Proposal',  desc:'Win more contracts on Upwork'        },
      { icon:'🔗', label:'LinkedIn Outreach',desc:'Reach clients where they are'        },
      { icon:'📧', label:'Client Email',     desc:'Professional follow-ups and pitches' },
      { icon:'💡', label:'Prompt Generator', desc:'Better AI prompts for your work'     },
      { icon:'💰', label:'Rate Calculator',  desc:'Find your ideal freelance rate'      },
      { icon:'📑', label:'Invoice Generator',desc:'Professional invoices in seconds'    },
    ],
    cta: 'Start freelancing →',
  },
];

// ── Free tools grid ────────────────────────────────────────────────
const FREE_TOOLS = [
  { icon:'💰', label:'Finance Calculators', desc:'FIRE, RRSP, TFSA, Mortgage, GST/HST — 26 calculators', path:'/tools/finance',  color:T.teal   },
  { icon:'🌐', label:'Network Tools',       desc:'IP lookup, DNS checker, Port scanner, Ping, Whois',     path:'/tools/ip',       color:T.blue   },
  { icon:'⌨️', label:'Developer Utils',     desc:'JSON formatter, JWT decoder, Base64, UUID, Hash',       path:'/tools/dev',      color:T.green  },
  { icon:'📋', label:'Business Tools',      desc:'Invoice builder, GST calculator, Payroll estimator',    path:'/tools/business', color:T.orange },
  { icon:'📝', label:'Text Tools',          desc:'Word counter, Case converter, Lorem ipsum, Diff',       path:'/tools/text',     color:T.yellow },
  { icon:'📁', label:'File Tools',          desc:'PDF merge, Image converter, File compressor',           path:'/tools/files',    color:T.purple },
];

// ── Social proof items ─────────────────────────────────────────────
const PROOF = [
  { icon:'✉️', text:'"Cover letter generated in 8 seconds. Got an interview the next day."' },
  { icon:'🏆', text:'"First Upwork proposal written with this — client hired me the same week."' },
  { icon:'📊', text:'"Finally a FIRE calculator that actually works for Canadian numbers."' },
];

export default function HomePage() {
  const [mounted, setMounted] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => { setTimeout(() => setMounted(true), 60); }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        @keyframes pulse  { 0%,100%{opacity:.5} 50%{opacity:1} }
        @keyframes float  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      <div style={{ background:T.bg, minHeight:'100vh', fontFamily:T.sans, color:T.white }}>

        {/* ── HERO ────────────────────────────────────────────────── */}
        <section style={{ textAlign:'center', padding:'96px 24px 72px', position:'relative', overflow:'hidden' }}>

          {/* Orbs */}
          <div style={{ position:'absolute', top:-120, left:'15%',  width:500, height:500, borderRadius:'50%', background:'rgba(0,200,240,.05)',    filter:'blur(90px)', pointerEvents:'none' }}/>
          <div style={{ position:'absolute', top:-80,  right:'10%', width:400, height:400, borderRadius:'50%', background:'rgba(183,140,255,.05)', filter:'blur(90px)', pointerEvents:'none' }}/>

          {/* Grid */}
          <div style={{ position:'absolute', inset:0, pointerEvents:'none', backgroundImage:`linear-gradient(rgba(255,255,255,.018) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.018) 1px,transparent 1px)`, backgroundSize:'60px 60px' }}/>

          <div style={{
            position:'relative',
            opacity:  mounted ? 1 : 0,
            transform:mounted ? 'translateY(0)' : 'translateY(20px)',
            transition:'all .65s ease',
          }}>

            {/* Eyebrow */}
            <div style={{
              display:'inline-flex', alignItems:'center', gap:8,
              padding:'5px 15px', borderRadius:20, marginBottom:26,
              background:'rgba(0,200,240,.08)', border:'1px solid rgba(0,200,240,.22)',
              fontSize:11.5, fontWeight:700, color:T.cyan,
              letterSpacing:'.1em', textTransform:'uppercase',
            }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background:T.cyan, boxShadow:`0 0 8px ${T.cyan}`, display:'inline-block', animation:'pulse 2s infinite' }}/>
              Powered by GPT-4o · Free 7-day trial
            </div>

            {/* Headline */}
            <h1 style={{
              fontSize:'clamp(2.4rem,5.5vw,4rem)', fontWeight:900,
              letterSpacing:'-.035em', lineHeight:1.07, marginBottom:20, color:T.white,
            }}>
              Land jobs and win clients<br/>
              <span style={{
                background:`linear-gradient(90deg,${T.cyan} 0%,${T.purple} 100%)`,
                WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text',
              }}>
                with AI on your side
              </span>
            </h1>

            {/* Subheadline */}
            <p style={{
              fontSize:'clamp(15px,2vw,18px)', color:T.muted,
              maxWidth:540, margin:'0 auto 38px', lineHeight:1.75,
            }}>
              AI-powered cover letters, job matching, Upwork proposals, and recruiter replies.
              Built for job seekers and freelancers. Plus 74+ free tools — no sign-up needed.
            </p>

            {/* CTA buttons */}
            <div style={{ display:'flex', justifyContent:'center', gap:12, flexWrap:'wrap', marginBottom:16 }}>
              <Link to="/register" style={{
                padding:'14px 30px', borderRadius:10, textDecoration:'none',
                background:`linear-gradient(135deg,${T.cyan},${T.blue})`,
                color:'#07080c', fontSize:15, fontWeight:800,
                display:'inline-flex', alignItems:'center', gap:8,
                boxShadow:`0 8px 24px rgba(0,200,240,.25)`,
              }}>
                🎯 Start free trial
              </Link>
              <Link to="/tools/finance" style={{
                padding:'14px 28px', borderRadius:10, textDecoration:'none',
                border:`1px solid ${T.border}`, background:T.bg2,
                color:T.white, fontSize:15, fontWeight:600,
              }}>
                Browse free tools
              </Link>
            </div>
            <div style={{ fontSize:12, color:T.dim }}>
              No credit card required · CA$7.99/mo after trial · Cancel anytime
            </div>

          </div>
        </section>

        {/* ── STATS BAR ───────────────────────────────────────────── */}
        <div style={{
          borderTop:`1px solid ${T.border}`, borderBottom:`1px solid ${T.border}`,
          background:T.bg2, display:'flex', justifyContent:'center', flexWrap:'wrap',
        }}>
          {[
            { value:'6',   label:'AI Job Search tools'  },
            { value:'6',   label:'AI Freelance tools'   },
            { value:'26',  label:'Finance calculators'  },
            { value:'74+', label:'Free tools total'     },
          ].map((s, i, arr) => (
            <div key={i} style={{
              flex:'1 1 120px', textAlign:'center', padding:'20px 16px',
              borderRight: i < arr.length - 1 ? `1px solid ${T.border}` : 'none',
            }}>
              <div style={{ fontSize:22, fontWeight:900, color:T.cyan, fontFamily:T.mono, marginBottom:3 }}>{s.value}</div>
              <div style={{ fontSize:11.5, color:T.muted }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── PORTAL CARDS ────────────────────────────────────────── */}
        <section style={{ maxWidth:1000, margin:'0 auto', padding:'80px 24px 56px' }}>

          <div style={{ textAlign:'center', marginBottom:52 }}>
            <h2 style={{
              fontSize:'clamp(1.5rem,3vw,2.1rem)', fontWeight:900,
              letterSpacing:'-.025em', marginBottom:10, color:T.white,
            }}>
              AI tools for your career
            </h2>
            <p style={{ fontSize:14, color:T.muted, maxWidth:440, margin:'0 auto' }}>
              Job hunting or freelancing — we have AI tools built for your exact workflow.
              One subscription unlocks both portals.
            </p>
          </div>

          <div style={{
            display:'grid',
            gridTemplateColumns:'repeat(auto-fit,minmax(400px,1fr))',
            gap:24,
          }}>
            {PORTALS.map(portal => (
              <PortalCard key={portal.id} portal={portal}/>
            ))}
          </div>

          {/* Smaller developer mention */}
          <div style={{
            marginTop:20, padding:'18px 22px', borderRadius:12,
            border:`1px solid ${T.border}`, background:T.bg2,
            display:'flex', alignItems:'center', justifyContent:'space-between',
            flexWrap:'wrap', gap:12,
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:24 }}>⌨️</span>
              <div>
                <div style={{ fontSize:13.5, fontWeight:700, color:T.white, marginBottom:2 }}>Developer AI Tools</div>
                <div style={{ fontSize:12, color:T.muted }}>SQL generator, Regex builder, API docs, Cron expressions — also included</div>
              </div>
            </div>
            <Link to="/developer" style={{
              padding:'7px 16px', borderRadius:8, textDecoration:'none', flexShrink:0,
              border:`1px solid ${T.green}35`, background:`${T.green}09`,
              color:T.green, fontSize:12.5, fontWeight:700,
            }}>
              Explore →
            </Link>
          </div>
        </section>

        {/* ── HOW IT WORKS ────────────────────────────────────────── */}
        <section style={{
          borderTop:`1px solid ${T.border}`,
          padding:'72px 24px',
          background:T.bg2,
        }}>
          <div style={{ maxWidth:800, margin:'0 auto', textAlign:'center' }}>
            <h2 style={{ fontSize:'clamp(1.3rem,2.5vw,1.8rem)', fontWeight:900, letterSpacing:'-.02em', marginBottom:48, color:T.white }}>
              From zero to application-ready in minutes
            </h2>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:32 }}>
              {[
                { step:'01', icon:'📄', title:'Upload your resume',    desc:'AI extracts your skills, experience, and achievements automatically' },
                { step:'02', icon:'🎯', title:'Paste a job description', desc:'Our matcher scores your fit and identifies missing keywords instantly' },
                { step:'03', icon:'✉️', title:'Generate your cover letter', desc:'Tailored to the role and company in under 10 seconds' },
                { step:'04', icon:'🚀', title:'Apply with confidence',  desc:'Reply to recruiters, follow up, and track your applications' },
              ].map(item => (
                <div key={item.step} style={{ textAlign:'center' }}>
                  <div style={{ fontSize:10, fontWeight:800, color:T.cyan, letterSpacing:'.15em', marginBottom:10, opacity:.6 }}>STEP {item.step}</div>
                  <div style={{ fontSize:28, marginBottom:12 }}>{item.icon}</div>
                  <div style={{ fontSize:13.5, fontWeight:700, color:T.white, marginBottom:7 }}>{item.title}</div>
                  <div style={{ fontSize:12.5, color:T.muted, lineHeight:1.65 }}>{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SOCIAL PROOF ────────────────────────────────────────── */}
        <section style={{ padding:'72px 24px' }}>
          <div style={{ maxWidth:900, margin:'0 auto' }}>
            <div style={{ textAlign:'center', marginBottom:36 }}>
              <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:T.dim, marginBottom:8 }}>What users say</div>
              <h2 style={{ fontSize:'clamp(1.2rem,2.5vw,1.6rem)', fontWeight:900, letterSpacing:'-.02em', color:T.white }}>Results that speak for themselves</h2>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:16 }}>
              {PROOF.map((p, i) => (
                <div key={i} style={{
                  padding:'20px 22px', borderRadius:12,
                  border:`1px solid ${T.border}`, background:T.bg2,
                }}>
                  <div style={{ fontSize:22, marginBottom:10 }}>{p.icon}</div>
                  <div style={{ fontSize:13.5, color:T.muted, lineHeight:1.7, fontStyle:'italic' }}>{p.text}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FREE TOOLS SECTION ──────────────────────────────────── */}
        <section style={{ borderTop:`1px solid ${T.border}`, padding:'72px 24px' }}>
          <div style={{ maxWidth:960, margin:'0 auto' }}>

            <div style={{ textAlign:'center', marginBottom:40 }}>
              <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:T.dim, marginBottom:10 }}>
                Also free — no account needed
              </div>
              <h2 style={{ fontSize:'clamp(1.3rem,2.5vw,1.7rem)', fontWeight:900, letterSpacing:'-.02em', color:T.white, marginBottom:10 }}>
                74+ free utility tools
              </h2>
              <p style={{ fontSize:13.5, color:T.muted, maxWidth:420, margin:'0 auto' }}>
                Finance calculators, network utilities, developer tools, and more.
                No sign-up, no limits.
              </p>
            </div>

            <div style={{
              display:'grid',
              gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',
              gap:12,
            }}>
              {FREE_TOOLS.map(tool => (
                <FreeToolCard key={tool.path} tool={tool}/>
              ))}
            </div>

          </div>
        </section>

        {/* ── PRICING CTA ─────────────────────────────────────────── */}
        <section style={{ padding:'0 24px 88px' }}>
          <div style={{
            maxWidth:660, margin:'0 auto', padding:'44px 40px',
            borderRadius:16, textAlign:'center',
            background:`linear-gradient(135deg,rgba(0,200,240,.07),rgba(183,140,255,.07))`,
            border:`1px solid rgba(0,200,240,.2)`,
          }}>
            <div style={{ fontSize:11.5, fontWeight:700, color:T.cyan, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:14 }}>
              One plan — Job Search + Freelancer + Developer
            </div>
            <h3 style={{ fontSize:'clamp(1.3rem,3vw,1.8rem)', fontWeight:900, color:T.white, marginBottom:10, letterSpacing:'-.02em' }}>
              Start with a 7-day free trial
            </h3>
            <p style={{ fontSize:13.5, color:T.muted, lineHeight:1.75, marginBottom:26, maxWidth:460, margin:'0 auto 26px' }}>
              Access all three AI portals. No credit card required.
              Cancel before the trial ends and pay nothing.
            </p>
            <div style={{ display:'flex', justifyContent:'center', gap:12, flexWrap:'wrap', marginBottom:14 }}>
              <Link to="/register" style={{
                padding:'12px 28px', borderRadius:9, textDecoration:'none',
                background:`linear-gradient(135deg,${T.cyan},${T.blue})`,
                color:'#07080c', fontSize:14, fontWeight:800,
              }}>
                Start free trial →
              </Link>
              <Link to="/pricing" style={{
                padding:'12px 24px', borderRadius:9, textDecoration:'none',
                border:`1px solid ${T.border}`, background:T.bg2,
                color:T.white, fontSize:14, fontWeight:600,
              }}>
                See pricing
              </Link>
            </div>
            <div style={{ fontSize:12, color:T.dim }}>
              CA$7.99/mo after trial · AI + Chrome Extension unlocks all 3 extensions at CA$10.99/mo
            </div>
          </div>
        </section>

        {/* ── FOOTER ──────────────────────────────────────────────── */}
        <footer style={{
          borderTop:`1px solid ${T.border}`, padding:'28px 40px',
          background:T.bg2, display:'flex', justifyContent:'space-between',
          alignItems:'center', flexWrap:'wrap', gap:16,
        }}>
          <div style={{ fontSize:16, fontWeight:900, color:T.white, letterSpacing:'-.02em' }}>
            Tylent<span style={{ color:T.cyan }}>ed</span>
          </div>
          <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
            {[
              ['/job-search',    'Job Search'  ],
              ['/freelancer',    'Freelancer'  ],
              ['/developer',     'Developer'   ],
              ['/tools/finance', 'Finance'     ],
              ['/pricing',       'Pricing'     ],
              ['/blog',          'Blog'        ],
              ['/terms',         'Terms'       ],
              ['/privacy',       'Privacy'     ],
            ].map(([path, label]) => (
              <Link key={path} to={path} style={{ fontSize:12.5, color:T.muted, textDecoration:'none' }}>{label}</Link>
            ))}
          </div>
          <div style={{ fontSize:12, color:T.dim }}>© 2026 Tylented Corp · Surrey, BC</div>
        </footer>

      </div>
    </>
  );
}

// ── Portal Card ────────────────────────────────────────────────────
function PortalCard({ portal }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to={portal.path}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:'block', textDecoration:'none',
        padding:'30px 28px', borderRadius:14,
        border:`1.5px solid ${hovered ? portal.color + '55' : 'rgba(255,255,255,.07)'}`,
        background: hovered ? `${portal.color}07` : 'rgba(255,255,255,.02)',
        boxShadow: hovered ? `0 16px 48px ${portal.glow}` : 'none',
        transform: hovered ? 'translateY(-4px)' : 'none',
        transition:'all .2s', position:'relative',
      }}
    >
      {/* Badge */}
      {portal.badge && (
        <div style={{
          position:'absolute', top:18, right:18,
          padding:'3px 10px', borderRadius:20,
          background:`${portal.color}18`, border:`1px solid ${portal.color}40`,
          fontSize:10.5, fontWeight:700, color:portal.color,
          textTransform:'uppercase', letterSpacing:'.06em',
        }}>
          {portal.badge}
        </div>
      )}

      {/* Icon */}
      <div style={{ fontSize:38, marginBottom:14, display:'inline-block', animation: hovered ? 'float 2s ease-in-out infinite' : 'none' }}>
        {portal.icon}
      </div>

      {/* Title + tagline */}
      <div style={{ fontSize:20, fontWeight:900, color:'#dce6fa', marginBottom:4, letterSpacing:'-.01em' }}>
        {portal.label}
      </div>
      <div style={{ fontSize:11.5, fontWeight:700, color:portal.color, marginBottom:14, textTransform:'uppercase', letterSpacing:'.08em' }}>
        {portal.tagline}
      </div>

      {/* Description */}
      <p style={{ fontSize:13.5, color:'rgba(220,230,250,.55)', lineHeight:1.72, marginBottom:20 }}>
        {portal.desc}
      </p>

      {/* Tools list */}
      <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:24 }}>
        {portal.tools.map(tool => (
          <div key={tool.label} style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{
              width:22, height:22, borderRadius:'50%', flexShrink:0,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:11, background:`${portal.color}15`, border:`1px solid ${portal.color}30`,
            }}>{tool.icon}</span>
            <div>
              <span style={{ fontSize:12.5, fontWeight:600, color:'#dce6fa' }}>{tool.label}</span>
              <span style={{ fontSize:11.5, color:'rgba(220,230,250,.35)', marginLeft:8 }}>{tool.desc}</span>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div style={{
        fontSize:14, fontWeight:700, color:portal.color,
        display:'flex', alignItems:'center', gap:5,
      }}>
        {portal.cta}
        <span style={{ transition:'transform .2s', transform: hovered ? 'translateX(5px)' : 'none', display:'inline-block' }}>→</span>
      </div>
    </Link>
  );
}

// ── Free Tool Card ─────────────────────────────────────────────────
function FreeToolCard({ tool }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to={tool.path}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:'flex', alignItems:'center', gap:14,
        padding:'14px 16px', borderRadius:10, textDecoration:'none',
        border:`1px solid ${hovered ? tool.color + '40' : 'rgba(255,255,255,.07)'}`,
        background: hovered ? `${tool.color}06` : 'rgba(255,255,255,.02)',
        transition:'all .15s',
      }}
    >
      <span style={{ fontSize:26, flexShrink:0 }}>{tool.icon}</span>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:13.5, fontWeight:700, color:'#dce6fa', marginBottom:3 }}>{tool.label}</div>
        <div style={{ fontSize:11.5, color:'rgba(220,230,250,.4)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{tool.desc}</div>
      </div>
    </Link>
  );
}
